// Canvas size/pricing constants and utility functions
// Shared between CanvasProductDetail and related components

export const PORTRAIT_SIZES = [
  { w: 40, h: 60, shopifyOption: "40x60cm" },
  { w: 50, h: 70, shopifyOption: "50x70cm" },
  { w: 60, h: 90, shopifyOption: "60x90cm" },
  { w: 75, h: 100, shopifyOption: "75x100cm" },
  { w: 100, h: 150, shopifyOption: "100x150cm" },
];

export const LANDSCAPE_SIZES = [
  { w: 60, h: 40, shopifyOption: "60x40cm" },
  { w: 70, h: 50, shopifyOption: "70x50cm" },
  { w: 90, h: 60, shopifyOption: "90x60cm" },
  { w: 100, h: 75, shopifyOption: "100x75cm" },
  { w: 150, h: 100, shopifyOption: "150x100cm" },
];

export const SQUARE_SIZES = [
  { w: 50, h: 50, shopifyOption: "50x50cm" },
  { w: 60, h: 60, shopifyOption: "60x60cm" },
  { w: 80, h: 80, shopifyOption: "80x80cm" },
  { w: 100, h: 100, shopifyOption: "100x100cm" },
  { w: 120, h: 120, shopifyOption: "120x120cm" },
  { w: 150, h: 150, shopifyOption: "150x150cm" },
];

export const FRAMES = [
  { id: "none", label: "Unframed", shopifyOption: "Unframed" },
  { id: "black", label: "Black Frame", shopifyOption: "Black Frame" },
  { id: "natural", label: "Natural Wood", shopifyOption: "Natural Wood" },
];

export const PRESET_SIZE_PRICES = {
  portrait: { "40×60": 39.99, "50×70": 49.99, "60×90": 69.99, "75×100": 89.99, "100×150": 149.99 },
  landscape: { "60×40": 39.99, "70×50": 49.99, "90×60": 69.99, "100×75": 89.99, "150×100": 149.99 },
  square: { "50×50": 44.99, "60×60": 59.99, "80×80": 79.99, "100×100": 99.99, "120×120": 129.99, "150×150": 179.99 },
};

export const FRAME_SURCHARGES = { none: 0, black: 20, natural: 25 };

import { Image, Frame, Home } from "lucide-react";

export const PREVIEW_MODES = [
  { id: "artwork", label: "Artwork", icon: Image },
  { id: "framed", label: "Framed", icon: Frame },
  { id: "room", label: "Preview", icon: Home },
];

export const PREVIEW_MODE_COPY = {
  artwork: "See the artwork cleanly on its own before choosing a finish.",
  framed: "Preview how the selected frame finish changes the presentation.",
  room: "Check the scale and overall presence in a styled interior.",
};

export function detectOrientation(images) {
  if (!images || images.length === 0) return "mixed";
  const img = images[0];
  const w = img.width || 0;
  const h = img.height || 0;
  if (!w || !h) return "mixed";
  const ratio = w / h;
  if (ratio > 1.15) return "landscape";
  if (ratio < 0.87) return "portrait";
  return "square";
}

export function getSizes(orientation) {
  if (orientation === "landscape") return LANDSCAPE_SIZES;
  if (orientation === "portrait") return PORTRAIT_SIZES;
  if (orientation === "square") return SQUARE_SIZES;
  return [{ w: 50, h: 70 }, { w: 60, h: 90 }, { w: 100, h: 100 }, { w: 100, h: 75 }, { w: 120, h: 120 }];
}

export function sizeKey(s) {
  return `${s.w}\u00d7${s.h}`;
}

export function defaultSize(orientation) {
  if (orientation === "landscape") return sizeKey({ w: 90, h: 60 });
  if (orientation === "portrait") return sizeKey({ w: 60, h: 90 });
  if (orientation === "square") return sizeKey({ w: 80, h: 80 });
  return sizeKey({ w: 60, h: 90 });
}

export function resolveVariant(variants, sizeOption, frameOption) {
  if (!variants || variants.length <= 1) return null;
  const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, "");
  const sizeLower = norm(sizeOption);
  const frameLower = norm(frameOption);
  if (!sizeLower) return null;
  return (
    variants.find((v) => {
      if (v.option1 != null) {
        const opt1 = norm(v.option1);
        const opt2 = norm(v.option2 || "");
        const isDefaultOpt2 = !opt2 || opt2 === "defaulttitle";
        return opt1 === sizeLower && (isDefaultOpt2 || opt2 === frameLower);
      }
      const t = norm(v.title);
      return t.includes(sizeLower) && t.includes(frameLower);
    }) || null
  );
}

export function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&[^;]+;/g, " ").trim();
}

export function formatCurrency(amount, currencyCode = "GBP") {
  const sym = currencyCode === "GBP" ? "£" : "$";
  return `${sym}${amount.toFixed(2)}`;
}

export function getInternalPresetPrice(orientation, sizeObj, frameId) {
  if (!sizeObj) return null;
  const key = sizeKey(sizeObj);
  const basePrice = PRESET_SIZE_PRICES[orientation]?.[key] || null;
  if (basePrice === null) return null;
  const surcharge = FRAME_SURCHARGES[frameId] ?? 0;
  return Math.round((basePrice + surcharge) * 100) / 100;
}
